// One practice of the lesson: its heading, instruction and the information
// given with it, the questions, and "Check". A check says which questions were
// right, the score, and whether the practice is passed (every answer right);
// "Try again" clears the answers for another go. A practice passed once stays
// passed, whatever a later attempt scores — as the lesson's gate counts it.

import { useEffect, useId, useRef, useState } from 'react'

import { post } from '../../../api'
import { BlockView } from '../../../components/Blocks'
import { useT } from '../../../i18n'
import practice from '../../../i18n/practice'
import { RichText } from '../../../lib/richtext'
import { Icon, messageOf } from '../../../lib/ui'
import { asBlock, isAnswered, NO_ANSWER, submission } from './model'
import type { Answer, AttemptResult, LearnerPractice, PracticeKind } from './model'
import { Question } from './Questions'

/** A practice kind's name in the reader's language. */
export function useKindLabel() {
  const t = useT(practice)
  return (kind: PracticeKind): string => {
    switch (kind) {
      case 'multiple_choice':
        return t('kindMultiple')
      case 'single_choice':
        return t('kindSingle')
      case 'fill_blanks':
        return t('kindFill')
      case 'select_options':
        return t('kindSelect')
    }
  }
}

export function PracticeItem({
  item,
  number,
  onChecked,
}: {
  item: LearnerPractice
  /** Its place among the lesson's practices, from 1. */
  number: number
  /** The server's verdict on an attempt, for the lesson to count passes by. */
  onChecked: (result: AttemptResult) => void
}) {
  const t = useT(practice)
  const kindLabel = useKindLabel()
  const headId = useId()
  const resultRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLOListElement>(null)

  const [answers, setAnswers] = useState<Record<string, Answer>>({})
  const [result, setResult] = useState<AttemptResult | null>(null)
  const [missing, setMissing] = useState<ReadonlySet<string>>(new Set())
  const [sending, setSending] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)
  // Where focus goes once the page has caught up: the verdict after a check,
  // the first question after "Try again".
  const [focusOn, setFocusOn] = useState<'result' | 'first' | null>(null)

  useEffect(() => {
    if (focusOn === 'result') resultRef.current?.focus()
    if (focusOn === 'first' && item.questions[0]) focusQuestion(item.questions[0].id)
    if (focusOn) setFocusOn(null)
    // focusQuestion reads only refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusOn, item.questions])

  const verdicts = new Map(result?.questions.map((q) => [q.question_id, q.correct]))
  const heading = item.prompt.trim()

  function answer(questionId: string, next: Answer) {
    setAnswers((prev) => ({ ...prev, [questionId]: next }))
    if (missing.has(questionId)) {
      const rest = new Set(missing)
      rest.delete(questionId)
      setMissing(rest)
      // "Answer every question first" is no longer true once the last
      // unanswered one is taken care of.
      if (rest.size === 0) setFailure(null)
    }
  }

  /** Brings a question into view with its first control focused. */
  function focusQuestion(questionId: string) {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-question="${CSS.escape(questionId)}"]`)
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    el?.querySelector<HTMLElement>('input:not(:disabled), select:not(:disabled)')?.focus({ preventScroll: true })
  }

  async function check() {
    if (sending) return
    const open = item.questions.filter((q) => !isAnswered(q, answers[q.id])).map((q) => q.id)
    if (open.length > 0) {
      setMissing(new Set(open))
      setFailure(t('answerAll', { n: open.length }))
      focusQuestion(open[0]!)
      return
    }
    setMissing(new Set())
    setFailure(null)
    setSending(true)
    try {
      const res = await post<AttemptResult>(`/learn/practices/${item.id}/submit`, submission(item.questions, answers))
      setResult(res)
      onChecked(res)
      setFocusOn('result')
    } catch (err) {
      setFailure(messageOf(err, practice))
    } finally {
      setSending(false)
    }
  }

  function again() {
    setResult(null)
    setAnswers({})
    setMissing(new Set())
    setFailure(null)
    setFocusOn('first')
  }

  const last = item.last_attempt
  return (
    <article className={item.passed ? 'practice is-passed' : 'practice'} aria-labelledby={headId}>
      <header className="practice__head">
        <span className="practice__number">{t('number', { n: number })}</span>
        <span className="practice__kind">{kindLabel(item.kind)}</span>
        {item.passed && (
          <span className="practice__badge">
            <Icon name="check" size={14} />
            {t('passedBadge')}
          </span>
        )}
      </header>
      {/* Without a heading of its own a practice is still named, for a screen
          reader, by its place; the number is already on screen above. */}
      <h3 id={headId} className={heading ? 'practice__title' : 'visually-hidden'}>
        {heading || t('practiceN', { n: number })}
      </h3>

      {item.instruction_html && <RichText html={item.instruction_html} className="practice__instruction" />}

      {item.attachments.length > 0 && (
        <div className="practice__info">
          {item.attachments.map((a, i) => (
            <BlockView key={a.id} block={asBlock(a, i)} trackProgress={false} />
          ))}
        </div>
      )}

      <ol ref={listRef} className="practice__questions">
        {item.questions.map((q, i) => (
          <li key={q.id}>
            <Question
              practiceId={item.id}
              index={i}
              question={q}
              answer={answers[q.id] ?? NO_ANSWER}
              verdict={verdicts.get(q.id)}
              missing={missing.has(q.id)}
              disabled={sending || result !== null}
              onAnswer={(next) => answer(q.id, next)}
            />
          </li>
        ))}
      </ol>

      {result && (
        <div
          ref={resultRef}
          className={result.passed ? 'practice__result is-passed' : 'practice__result'}
          role="status"
          tabIndex={-1}
        >
          <Icon name={result.passed ? 'check' : 'alert'} size={20} />
          <div>
            <p className="practice__result-title">{result.passed ? t('passedTitle') : t('failedTitle')}</p>
            <p className="practice__result-score">
              {t('score', { correct: result.correct, total: result.total, pct: result.score_pct })}
            </p>
            {!result.passed && <p className="practice__result-hint">{t('failedHint')}</p>}
          </div>
        </div>
      )}

      {failure && (
        <p className="practice__failure" role="alert">
          {failure}
        </p>
      )}

      <div className="practice__actions">
        {result ? (
          <button type="button" className="button button--quiet" onClick={again}>
            <Icon name="refresh" size={18} />
            {t('tryAgain')}
          </button>
        ) : (
          <button type="button" className="button" onClick={() => void check()} disabled={sending}>
            {sending ? t('checking') : t('check')}
          </button>
        )}
        {!result && last && (
          <span className="practice__last">
            {t('lastAttempt', { correct: last.correct, total: last.total, pct: last.score_pct })}
          </span>
        )}
      </div>
    </article>
  )
}
